import { cn, cva } from 'noddi-ui-common';
import { NoddiButton, NoddiDateRangePicker, Popover, PopoverContent, PopoverTrigger } from 'noddi-ui-web';
import { NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { useId, useState } from 'react';
import { DateRange } from 'react-day-picker';

import type { SeasonDateRangeMode, SeasonDateRangeWindow } from './types';

interface Props {
  label?: string;
  dateMode: SeasonDateRangeMode;
  onDateModeChange: (mode: SeasonDateRangeMode) => void;
  seasons?: SeasonDateRangeWindow[];
  selectedSeason?: SeasonDateRangeWindow | null;
  onSeasonSelect: (season: SeasonDateRangeWindow) => void;
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
  align?: 'start' | 'end';
  className?: string;
}

const tabVariants = cva('flex-1 px-4 py-2 text-sm font-medium focus-visible:ring-2 focus-visible:ring-ring', {
  variants: {
    active: {
      true: 'border-b-2 border-foreground text-foreground',
      false: 'text-muted-foreground hover:text-foreground'
    }
  }
});

const seasonOptionVariants = cva('flex w-full flex-col items-start gap-1 rounded-md px-3 py-2 text-left text-sm', {
  variants: {
    selected: {
      true: 'bg-accent font-medium text-accent-foreground',
      false: 'text-foreground hover:bg-muted'
    }
  }
});

const seasonDateVariants = cva('text-xs', {
  variants: {
    selected: {
      true: 'opacity-80',
      false: 'text-muted-foreground'
    }
  }
});

export const SeasonDateRangePicker = ({
  label,
  dateMode,
  onDateModeChange,
  seasons,
  selectedSeason,
  onSeasonSelect,
  dateRange,
  onDateRangeChange,
  align = 'end',
  className
}: Props) => {
  const [open, setOpen] = useState(false);
  const controlId = useId();
  const labelId = `${controlId}-label`;
  const hasCustomRange = dateMode === 'custom' && dateRange?.from && dateRange.to;
  const triggerLabel =
    dateMode === 'season' && selectedSeason
      ? `${selectedSeason.name} (${formatSeasonDate(selectedSeason.dateFrom)} – ${formatSeasonDate(selectedSeason.dateTo)})`
      : hasCustomRange
        ? `${noddiFormatDate(dateRange.from, NoddiDateFormats.DATE)} – ${noddiFormatDate(dateRange.to, NoddiDateFormats.DATE)}`
        : 'Select date range';

  const control = (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <NoddiButton
          id={controlId}
          variant='outline'
          size={label ? 'default' : 'small'}
          startIcon='Calendar'
          aria-haspopup='dialog'
          aria-expanded={open}
          aria-labelledby={label ? `${labelId} ${controlId}` : undefined}
          className={cn(
            label && 'h-9 w-full min-w-70 justify-start font-normal',
            label && !selectedSeason && !hasCustomRange && 'text-muted-foreground'
          )}
        >
          {triggerLabel}
        </NoddiButton>
      </PopoverTrigger>
      <PopoverContent className='w-auto p-0' align={align}>
        <div className='flex flex-col'>
          <div className='flex border-b border-border'>
            <button
              type='button'
              className={tabVariants({ active: dateMode === 'season' })}
              onClick={() => onDateModeChange('season')}
            >
              Season
            </button>
            <button
              type='button'
              className={tabVariants({ active: dateMode === 'custom' })}
              onClick={() => onDateModeChange('custom')}
            >
              Custom
            </button>
          </div>
          {dateMode === 'season' && (
            <div className='flex max-h-60 flex-col gap-1 overflow-y-auto p-2'>
              {seasons?.map((season) => (
                <button
                  key={season.name}
                  type='button'
                  className={seasonOptionVariants({ selected: selectedSeason?.name === season.name })}
                  onClick={() => {
                    onSeasonSelect(season);
                    setOpen(false);
                  }}
                >
                  <span className='font-medium'>{season.name}</span>
                  <span className={seasonDateVariants({ selected: selectedSeason?.name === season.name })}>
                    {formatSeasonDate(season.dateFrom)} – {formatSeasonDate(season.dateTo)}
                  </span>
                </button>
              ))}
              {seasons?.length === 0 && (
                <p className='px-3 py-2 text-sm text-muted-foreground'>No seasons are available.</p>
              )}
            </div>
          )}
          {dateMode === 'custom' && (
            <div className='p-3'>
              <NoddiDateRangePicker dateRange={dateRange} onDateChange={onDateRangeChange} />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );

  if (!label) {
    return control;
  }

  return (
    <div className={cn('flex w-full min-w-70 flex-col gap-0.5 sm:w-auto', className)}>
      <label id={labelId} htmlFor={controlId} className='text-sm'>
        {label}
      </label>
      {control}
    </div>
  );
};

const formatSeasonDate = (value: string) => noddiFormatDate(value, NoddiDateFormats.DATE);
